import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import track from 'react-tracking';
import { reduxForm, formValues } from 'redux-form';
import { v4 as uuidv4 } from 'uuid';
import BEMHelper from '@ynap/bem-helper';
import Field from '@ynap/field';
import { PasswordInput } from '@ynap/password-input';
import { Button, SimpleAsyncButton } from '@ynap/button';
import { Tooltip } from '@ynap/notification';
import { submitAndCollateErrorsPromise } from '@ynap/form-utils';
import { withOptimizely } from '@ynap/optimizely-utils';
import { trackReadPrivacyPolicy, trackOptIn } from '../analytics';
import collateErrors from './collateErrors';
import validate from '../RegistrationForm/validate';
import TcMessage from '../RegistrationForm/TcMessage';
import { Consent, emailInfoOverlay } from '../RegistrationForm/RegistrationForm';
import TcOverlay from '../RegistrationForm/TcOverlay';


const bem = new BEMHelper('GuestRegistrationForm');

@track()
class GuestRegistrationForm extends PureComponent {
    static propTypes = {
        component: PropTypes.node,
        form: PropTypes.string,
        submitting: PropTypes.bool,
        children: PropTypes.node,
        email: PropTypes.string.isRequired,
        error: PropTypes.string,
        continueShoppingPath: PropTypes.string,
        className: PropTypes.string,
        onSubmit: PropTypes.func,
        handleSubmit: PropTypes.func,
        change: PropTypes.func,
        messages: PropTypes.shape({
            guestRegistrationForm: PropTypes.shape({
                benefits: PropTypes.func,
                createPassword: PropTypes.func,
                register: PropTypes.func,
            }).isRequired,
            registrationForm: {
                firstName: PropTypes.func,
                lastName: PropTypes.func,
            },
            passwordRequirements: PropTypes.object,
        }).isRequired,
        locale: PropTypes.shape({
            id: PropTypes.string,
        }),
        links: PropTypes.shape({
            privacyPolicy: PropTypes.string,
            termsAndConditions: PropTypes.string,
        }),
        enableGenderSelection: PropTypes.bool,
        ageConsent: PropTypes.bool,
        tcConsent: PropTypes.bool,
        gdprConsent: PropTypes.bool,
        checkoutAnalytics: PropTypes.object.isRequired,
        multipleLanguageCountries: PropTypes.object,
        optimizely: PropTypes.object.isRequired,
        supportedLanguages: PropTypes.arrayOf(PropTypes.string),
        _reduxForm: PropTypes.shape({
            values: PropTypes.object,
        }),
        config: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.state = {
            maMarketingConsent: null,
            preferredLanguage: null,
            showOverlay: false,
            isRegistrationComplete: false,
            showTcOverlay: false,
            showNewsletterOverlay: false,
            tmSessionId: null
        };
        this.toggleOverlay = this.toggleOverlay.bind(this);
        this.handleToggleMktConsent = this.handleToggleMktConsent.bind(this);
        this.handleTcOverlayBodyClick = this.handleTcOverlayBodyClick.bind(this);
        this.handleTcOverlay = this.handleTcOverlay.bind(this);
        this.handleNewsletterOverlayBodyClick = this.handleNewsletterOverlayBodyClick.bind(this);
        this.handleNewsletterOverlay = this.handleNewsletterOverlay.bind(this);
    }

    static defaultProps = {
        component: 'form',
    };

    componentDidMount() {
        const { threatMetrix } = this.props.config?.head || {};
        const { orgId, url: tmUrl } = threatMetrix || {};

        if (window?.tm?.prfl && !!threatMetrix) {
            const tmSessionId = uuidv4();

            this.setState({ tmSessionId });
            window.tm.prfl(tmUrl, orgId, tmSessionId);
        }
    }

    handleNewsletterOverlay() {
        this.setState({ showNewsletterOverlay: true });
    }

    handleNewsletterOverlayBodyClick() {
        this.setState({ showNewsletterOverlay: false });
    }

    handleTcOverlay() {
        this.setState({ showTcOverlay: true });
    }

    handleTcOverlayBodyClick() {
        this.setState({ showTcOverlay: false });
    }

    toggleOverlay() {
        this.setState({ showOverlay: !this.state.showOverlay });
    }

    handleToggleMktConsent(maMarketingConsent, preferredLanguage) {
        this.setState({ ...(maMarketingConsent && { maMarketingConsent }), ...(preferredLanguage && { preferredLanguage }) });
    }

    @track((_props, _state, [maMarketingConsent]) => trackOptIn(maMarketingConsent))
    trackOptInEvent(_maMarketingConsent) {}

    submit = (values, delay) => {
        const { onSubmit, multipleLanguageCountries, locale: { country = 'gb', language = 'en' } = {}, optimizely, config } = this.props;
        const tmEnabled = !!config?.head?.threatMetrix;
        const { maMarketingConsent } = this.state;
        const fallbackLangISO =
            multipleLanguageCountries && multipleLanguageCountries.hasOwnProperty(country)
                ? multipleLanguageCountries[country].filter((langISO) => langISO.includes(language))[0]
                : language;
        const selectedPreferredLanguage = (maMarketingConsent === 'true' && this.state.preferredLanguage) || fallbackLangISO;

        // FECC-424 TON Post Checkout Customer Registration A/B test
        return submitAndCollateErrorsPromise(onSubmit({ ...values, tmEnabled, tmSessionId: this.state.tmSessionId, preferredLanguage: selectedPreferredLanguage }, delay, false), values, this.props).then(
            () => {
                this.setState({ isRegistrationComplete: true });
                this.trackOptInEvent(values?.maMarketingConsent === 'true')
                optimizely?.track('oc_registration_success');
                if (values?.maMarketingConsent === 'true') {
                    optimizely?.track('optin_registration_orderconfirmation');
                    optimizely?.track('optin_registration');
                    optimizely?.track('global_opt-in');
                }
            },
        );
    };

    render() {
        const {
            component: Component,
            form,
            className,
            error,
            messages,
            submitting,
            handleSubmit,
            children,
            locale,
            optimizely,
            links,
            continueShoppingPath,
            checkoutAnalytics,
            gdprConsent,
            ageConsent,
            tcConsent,
            change: changeField,
            supportedLanguages,
            enableGenderSelection,
            _reduxForm,
            config,
        } = this.props;
        
        const { isRegistrationComplete, preferredLanguage } = this.state;
        const { values } = _reduxForm;

        return (
            <Component id={form} className={bem(null, { submitting }, className)} onSubmit={handleSubmit && handleSubmit(this.submit)}>
                {isRegistrationComplete && (
                    <div>
                        <div className={bem('heading', 'welcome')}>{messages.guestRegistrationForm.welcome()}</div>
                        <div className={bem('heading', 'success')}>{messages.guestRegistrationForm.success()}</div>
                    </div>
                )}
                {!isRegistrationComplete && (
                    <>
                        <div className={bem('heading', 'title')}>{messages.guestRegistrationForm.registrationTitle()}</div>
                        <div className={bem('benefits')}>
                            {['tracking', 'ease', 'speed'].map((benefitType) => (
                                <span key={benefitType} className={bem('benefit', benefitType)}>
                                    {messages.guestRegistrationForm.benefits({ benefitType })}
                                </span>
                            ))}
                        </div>
                        <div className={bem('form')}>
                            <span className={bem('heading', 'detailsTitle')}>{messages.guestRegistrationForm.accountDetails()}</span>
                            <div className={bem('details')}>
                                <Field
                                    className={bem('detailsField')}
                                    name="firstName"
                                    type="text"
                                    label={messages.registrationForm?.firstName()}
                                    autoComplete="given-name"
                                />
                                <Field
                                    className={bem('detailsField')}
                                    name="lastName"
                                    type="text"
                                    label={messages.registrationForm?.lastName()}
                                    autoComplete="family-name"
                                />
                            </div>
                            <Field
                                className={bem('passwordField')}
                                component={PasswordInput}
                                name="logonPassword"
                                label={messages.guestRegistrationForm.createPassword()}
                                help={messages.guestRegistrationForm.passwordHelp()}
                                optimizely={optimizely}
                            />
                            {!gdprConsent && (
                                <TcMessage className={bem('terms')} messages={messages} localeId={locale.id} onPrivacyClick={trackReadPrivacyPolicy} />
                            )}
                            {children}
                        </div>
                        {handleSubmit && (
                            <Consent
                                className={bem('marketingConsent')}
                                messages={messages}
                                links={links}
                                maMarketingConsent={values?.maMarketingConsent}
                                handleToggleMktConsent={this.handleToggleMktConsent}
                                locale={locale}
                                checkoutAnalytics={checkoutAnalytics}
                                config={config}
                                gdprConsent={gdprConsent}
                                enableAgeConsent={ageConsent}
                                enableTcConsent={tcConsent}
                                changeField={changeField}
                                form={form}
                                field="maMarketingConsent"
                                showTermsAndConditions={false}
                                supportedLanguages={supportedLanguages}
                                preferredLanguage={preferredLanguage}
                                enableGenderSelection={enableGenderSelection}
                                handleNewsletterOverlay={this.handleNewsletterOverlay}
                                handleTcOverlay={this.handleTcOverlay}
                                handleEmailOverlay={this.toggleOverlay}
                                optimizely={optimizely}
                            />
                        )}
                    </>
                )}
                {handleSubmit && (
                    <div className={bem('ctaGroup')}>
                        {isRegistrationComplete ? (
                            <Button
                                className={bem('cta')}
                                type="primary"
                                component="a"
                                href={`/${locale.id}/account`}
                                // FECC-424 TON Post Checkout Customer Registration A/B test
                                onClick={() => optimizely?.track('oc_post_registration_cta_click')}
                            >
                                {messages.guestRegistrationForm.viewMyAccount()}
                            </Button>
                        ) : (
                            <Tooltip className={bem('error')} type="error" message={error} defaultOpen={!submitting && !!error} key={submitting || error}>
                                <SimpleAsyncButton
                                    className={bem('cta')}
                                    containerClass={bem('buttonWrapper')}
                                    type="primary"
                                    loading={submitting}
                                    copy={messages.guestRegistrationForm.register()}
                                />
                            </Tooltip>
                        )}
                        <Button
                            className={bem('cta')}
                            type="secondary"
                            component="a"
                            href={`/${locale?.id}${continueShoppingPath}`}
                            // FECC-424 TON Post Checkout Customer Registration A/B test
                            onClick={() => optimizely?.track('oc_registration_secondary_cta_click')}
                        >
                            {messages.guestRegistrationForm.shopNewIn()}
                        </Button>
                    </div>
                )}
                {this.state.showOverlay && emailInfoOverlay(messages, locale, this.toggleOverlay)}
                {this.state.showTcOverlay && <TcOverlay messages={messages} handleClose={this.handleTcOverlayBodyClick} locale={locale} />}
                {this.state.showNewsletterOverlay && (
                    <TcOverlay messages={messages} handleClose={this.handleNewsletterOverlayBodyClick} locale={locale} isNewsletterTc />
                )}
            </Component>
        );
    }
}

export const GuestRegistrationFormWithValues = withOptimizely(formValues({ logonEmailValue: 'logonEmail' })(GuestRegistrationForm));

export default reduxForm({
    form: 'guestRegistration',
    collateErrors,
    validate,
})(GuestRegistrationFormWithValues);
